<template>
  <div class="app">
    <header>
      <!-- Header content goes here -->
    </header>

    <main>
      <h1>Streamline Your Property Management Today</h1>
      <p>Effortlessly manage rentals with our platform.</p>

      <div class="login-wrapper">
        <div class="login-container">
          <img src="../assets/circle.png" alt="Property Group Logo" class="login-logo">
          <v-form ref="form" v-model="valid" @submit.prevent="login()">
            <v-row>
              <v-col>
                <v-text-field
                  v-model="email"
                  dense
                  label="Email Address"
                  rounded
                  outlined
                  class="input"
                  :rules="emailRules"
                ></v-text-field>
                <v-text-field
                  :rules="passwordRules"
                  v-model="password"
                  dense
                  label="Password"
                  class="input"
                  rounded
                  outlined
                  :append-icon="show1 ? 'mdi-eye' : 'mdi-eye-off'"
                  :type="show1 ? 'text' : 'password'"
                  @click:append="show1 = !show1"
                ></v-text-field>
                <v-btn
                  type="submit"
                  class="white--text login-btn"
                  :loading="loading"
                  @click="login()"
                >
                  LogIn
                </v-btn>
                <div class="input forgot-password" @click="goToPage('/forgotPassword')">
                  Forgot Password?
                </div>
              </v-col>
            </v-row>
          </v-form>
        </div>
      </div>
    </main>

    <footer>
      <p>&copy; 2026 Property Group</p>
    </footer>
  </div>
</template>

<script>
import { auth } from "../firebaseConfig.js";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import { collection, getDocs, where, query, getFirestore } from "firebase/firestore";
import { firebaseApp } from "../firebaseConfig";
import { logEvent } from "@/utils/logEvent";  // ← This is all you need!

const db = getFirestore(firebaseApp);

export default {
  data() {
    return {
      email: '',
      password: '',
      valid: false,
      show1: false,
      loading: false,
      emailRules: [
        v => !!v || 'Email is required',
        v => /.+@.+\..+/.test(v) || 'Email must be valid'
      ],
      passwordRules: [
        v => !!v || 'Password is required',
        v => v.length >= 6 || 'Password must be at least 6 characters long'
      ]
    };
  },
  methods: {
    goToPage(path) {
      if (this.$route.path !== path) this.$router.push(path);
    },
    validate() {
      this.$refs.form.validate();
    },

    async login() {
      if (this.loading) return;

      // Auto-login from saved credentials (Remember Me)
      if (localStorage.getItem("email") && localStorage.getItem("password")) {
        this.email = localStorage.getItem("email");
        this.password = localStorage.getItem("password");
        this.valid = true;
      } else {
        this.validate();
      }

      if (!this.valid) return;

      this.loading = true;

      try {
        await signInWithEmailAndPassword(auth, this.email, this.password);
        await this.getUser(this.email, this.password);

      } catch (error) {
        // Log every failed login attempt
        await logEvent("Login Failed", this.email, "Authentication");

        let message = "Invalid email or password.";
        if (error.code === "auth/user-not-found") message = "User not found.";
        if (error.code === "auth/wrong-password") message = "Incorrect password.";
        if (error.code === "auth/too-many-requests") message = "Too many attempts. Try again later.";
        if (error.code === "auth/invalid-credential") message = "Invalid credentials.";

        this.$store.commit("displayResponse", { type: "error", message });
      } finally {
        this.loading = false;
      }
    },

    async getUser(email, password) {
      try {
        const q = query(collection(db, "adminUsers"), where("email", "==", email));
        const snapshot = await getDocs(q);

        if (snapshot.empty) throw new Error("User not in adminUsers collection");
        const userData = snapshot.docs[0].data();

        if (userData.AccountDisabled === true) {
          await logEvent("Login Blocked – Account Disabled", email, "Authentication");
          throw new Error("Account disabled");
        }

        // Success → Save session
        localStorage.setItem("currentUser", JSON.stringify(userData));
        localStorage.setItem("email", email);
        localStorage.setItem("password", password);

        // Log successful login
        await logEvent("User Logged In", email, "Authentication");

        this.$store.commit("displayResponse", {
          type: "success",
          message: `Welcome back, ${userData.name || "User"}!`
        });

        this.$router.replace("/dashboard");

      } catch (error) {
        await signOut(auth);
        localStorage.clear();

        const msg = error.message === "Account disabled"
          ? "Your account is disabled. Contact an administrator."
          : "Access denied.";

        this.$store.commit("displayResponse", { type: "error", message: msg });
      }
    }
  }
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body {
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  color: #F1C284;
}

.app {
  flex: 1;
  height: 100vh;
  width: 100vw;
  background-image: url("../assets/background.jpeg");
  background-size: cover;
  background-position: center;
  display: flex;
  flex-direction: column;
  font-family: Arial, sans-serif;
}

header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background-color: rgba(0, 0, 0, 0.7);
}

.logo-container {
  margin-left: 20px;
}

.logo {
  height: 100px;
}

nav ul {
  display: flex;
  list-style: none;
}

nav ul li {
  margin: 0 10px;
}

nav ul li a {
  text-decoration: none;
  color: #F1C284;
  font-size: 16px;
}

main {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.7);
  text-align: center;
}

h1, p {
  color: white;
}

h1 {
  font-size: 36px;
  margin-bottom: 20px;
}

p {
  font-size: 18px;
  margin-bottom: 20px;
}

.login-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

.login-container {
  background: white;
  padding: 40px;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.login-logo {
  height: 100px;
  margin-bottom: 20px;
}

.login-btn {
  background: linear-gradient(0deg,rgba(241, 194, 132, 1) 0%,rgba(241, 194, 132, 1) 100%);
  border-radius: 20px;
  width: 100%;
}

.input {
  width: 100%;
  margin: 10px 0;
}

.forgot-password {
  color: #0066cc;
  cursor: pointer;
  margin-top: 10px;
}

footer {
  padding: 20px;
  flex-shrink: 0;
  text-align: center;
}
</style>